import { fade } from '@material-ui/core/styles/colorManipulator';
import { agentAccentGradients } from '../../app/Theme';

const styles = theme => ({
    root: {
        flexGrow: 1,
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        minHeight: 68,
        paddingTop: theme.spacing(1.35),
        paddingBottom: theme.spacing(1.35),
        paddingLeft: theme.spacing(2.5),
        paddingRight: theme.spacing(2.5),
        flexWrap: 'nowrap',
        width: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        [theme.breakpoints.down('md')]: {
            gap: theme.spacing(1.05),
            minHeight: 62,
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
        },
        [theme.breakpoints.down('sm')]: {
            gap: theme.spacing(0.8),
            minHeight: 58,
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
            paddingLeft: theme.spacing(1.5),
            paddingRight: theme.spacing(1.5),
        }
    },
    grow: {
        flexGrow: 1,
    },
    brandGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.1),
        flexShrink: 1,
        flex: '0 1 auto',
        minWidth: 0,
        order: 0,
        [theme.breakpoints.down('sm')]: {
            gap: theme.spacing(0.7)
        }
    },
    appLink: {
        color: theme.palette.text.primary,
        textDecoration: 'inherit',
        transition: theme.transitions.create('color', { duration: theme.transitions.duration.short })
    },
    appBrandLockup: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.1),
        minWidth: 0,
    },
    appLogo: {
        width: theme.spacing(4.4),
        height: theme.spacing(4.4),
        display: 'block',
        flexShrink: 0,
        [theme.breakpoints.down('sm')]: {
            width: theme.spacing(3.7),
            height: theme.spacing(3.7),
        }
    },
    appTitle: {
        fontSize: '1.14rem',
        lineHeight: 1.1,
        fontWeight: 800,
        letterSpacing: '1.55px',
        color: theme.palette.text.primary,
        whiteSpace: 'nowrap',
        [theme.breakpoints.down('md')]: {
            fontSize: '1rem',
            letterSpacing: '1.25px',
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: '0.92rem',
            letterSpacing: '1px',
        }
    },
    agentLink: {
        textTransform: 'none',
        fontWeight: 700,
        lineHeight: 1.02,
        minWidth: 0,
        padding: 0,
        marginLeft: theme.spacing(0.45),
        border: 0,
        borderRadius: 0,
        backgroundColor: 'transparent',
        flexShrink: 1,
        '&:hover': {
            backgroundColor: 'transparent',
        }
    },
    agentGradientLabel: {
        background: theme.palette.type === 'light'
            ? agentAccentGradients.light
            : agentAccentGradients.dark,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        color: 'transparent',
        display: 'inline-block',
        fontSize: 'clamp(0.74rem, 0.18vw + 0.72rem, 0.92rem)',
        lineHeight: 1.02,
        maxWidth: '7ch',
        textAlign: 'center',
        whiteSpace: 'normal',
        textShadow: theme.palette.type === 'light' ? '0 0 1px rgba(0,0,0,0.25)' : 'none',
    },
    navLinks: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.45),
        flexWrap: 'nowrap',
        justifyContent: 'flex-end',
        minWidth: 0,
        flex: '0 1 auto',
        maxWidth: '100%',
        [theme.breakpoints.down('md')]: {
            gap: theme.spacing(0.22),
        },
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        }
    },
    navButton: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: 'clamp(0.68rem, 0.16vw + 0.66rem, 0.84rem)',
        color: theme.palette.text.primary,
        borderRadius: 0,
        padding: theme.spacing(0.32, 0.45),
        minWidth: 0,
        border: 0,
        backgroundColor: 'transparent',
        transition: theme.transitions.create(['color', 'opacity'], { duration: theme.transitions.duration.short }),
        '&:hover': {
            backgroundColor: 'transparent',
            opacity: 0.84
        },
        flexShrink: 1,
        lineHeight: 1.06,
        textAlign: 'center',
    },
    navButtonLabel: {
        whiteSpace: 'normal',
        lineHeight: 1.06,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        wordBreak: 'normal',
        overflowWrap: 'normal',
        maxWidth: '8.7ch',
        hyphens: 'none'
    },
    profileAvatar: {
        width: theme.spacing(3.5),
        height: theme.spacing(3.5)
    },
    toolbarActions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: theme.spacing(0.32),
        flexShrink: 1,
        flex: '1 1 auto',
        minWidth: 0,
        order: 2,
        flexWrap: 'nowrap',
        marginLeft: 'auto',
    },
    authButtons: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.8),
        flexWrap: 'nowrap',
        [theme.breakpoints.down('sm')]: {
            display: 'none'
        }
    },
    actionButton: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(0.58, 1.3),
        whiteSpace: 'nowrap',
        color: theme.palette.type === 'dark' ? theme.palette.common.white : 'inherit',
        borderColor: theme.palette.type === 'dark' ? fade(theme.palette.common.white, 0.28) : undefined,
        '&:hover': {
            borderColor: theme.palette.type === 'dark' ? fade(theme.palette.common.white, 0.4) : undefined,
            color: theme.palette.type === 'dark' ? theme.palette.common.white : undefined,
        },
        [theme.breakpoints.down('md')]: {
            fontSize: '0.76rem',
            padding: theme.spacing(0.46, 0.95)
        }
    },
    loginButton: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(0.58, 1.3),
        whiteSpace: 'nowrap',
        color: theme.palette.primary.main,
        borderColor: fade(theme.palette.primary.main, 0.62),
        backgroundColor: theme.palette.type === 'light' ? 'rgba(126, 103, 248, 0.06)' : 'transparent',
        '&:hover': {
            color: theme.palette.primary.dark,
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.type === 'light' ? 'rgba(126, 103, 248, 0.12)' : 'rgba(200, 177, 255, 0.08)',
        },
        [theme.breakpoints.down('md')]: {
            fontSize: '0.76rem',
            padding: theme.spacing(0.46, 0.95)
        }
    },
    iconAction: {
        padding: theme.spacing(0.82),
        flexShrink: 0,
        color: theme.palette.text.primary
    },
    profileActions: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0
    },
    mobileOnly: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'flex'
        }
    },
    navMenuPaper: {
        borderRadius: theme.spacing(2),
    },
    searchDialogPaper: {
        borderRadius: theme.spacing(2.5),
        background: theme.palette.type === 'dark'
            ? 'linear-gradient(145deg, rgba(28, 34, 46, 0.9), rgba(20, 24, 32, 0.88))'
            : 'linear-gradient(145deg, rgba(255, 255, 255, 0.92), rgba(245, 247, 252, 0.9))',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        backdropFilter: 'blur(14px) saturate(135%)',
    },
    searchDialogContent: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1.1),
    },
    searchDialogField: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        minHeight: theme.spacing(5.4),
        borderRadius: 999,
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(203, 213, 225, 0.14)' : 'rgba(15, 23, 42, 0.08)',
        border: `1px solid ${fade(theme.palette.divider, theme.palette.type === 'dark' ? 0.95 : 1)}`,
        backdropFilter: 'blur(14px) saturate(135%)',
    },
    searchDialogIcon: {
        width: theme.spacing(6.5),
        height: '100%',
        position: 'absolute',
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.type === 'dark' ? '#d7dfef' : '#374151',
    },
    searchDialogInputRoot: {
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.type === 'dark' ? '#e5ebf7' : '#374151',
        width: '100%',
    },
    searchDialogInput: {
        paddingTop: theme.spacing(1.05),
        paddingRight: theme.spacing(2),
        paddingBottom: theme.spacing(1.05),
        paddingLeft: theme.spacing(6.5),
        transition: theme.transitions.create(['width', 'color']),
        width: '100%',
        fontSize: '0.95rem',
        lineHeight: 1.4,
        color: theme.palette.type === 'dark' ? '#e5ebf7' : '#374151',
        '&::placeholder': {
            color: theme.palette.type === 'dark' ? '#cbd5e1' : '#4b5563',
            opacity: 1,
        },
    },
    alertContainer: {
        position: 'fixed',
        top: 0,
        left: '40%',
        zIndex: 1000,
    },
});

export default styles;
